import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { superAdminApi } from '../../services/superAdminApi';

const DnsInstructions = ({ domain, onClose }) => {
  const [dnsResults, setDnsResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (domain.domain_type === 'custom_domain') {
      checkDnsStatus();
    }
  }, [domain]);

  const checkDnsStatus = async () => {
    try {
      setLoading(true);
      const response = await superAdminApi.checkDomainDns(domain.id);
      setDnsResults(response.data.dns_results);
    } catch (error) {
      console.error('Failed to check DNS status:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const getInstructions = () => {
    if (domain.domain_type === 'subdomain') {
      return {
        title: 'Subdomain Configuration',
        description: 'This subdomain is automatically configured and should be active immediately.',
        records: domain.dns_records || [],
        isAutomatic: true
      };
    }

    return {
      title: 'Custom Domain DNS Setup',
      description: 'Configure the following DNS records with your domain provider to point your domain to our servers.',
      records: domain.dns_records || [
        {
          type: 'A',
          name: '@',
          value: '192.168.1.100', // This would come from config
          ttl: 300,
          description: 'Points your root domain to our server'
        },
        {
          type: 'CNAME',
          name: 'www',
          value: 'abfisaas.com',
          ttl: 300,
          description: 'Points www subdomain to our server'
        }
      ],
      isAutomatic: false
    };
  };

  const instructions = getInstructions();

  const getDnsStatusIcon = (record) => {
    if (!dnsResults) return null;
    
    const isCorrect = dnsResults.pointing_to_server || dnsResults.a_records?.some(r => r.ip === record.value);
    
    return isCorrect ? (
      <i className="fas fa-check-circle text-success" title="DNS record is correct"></i>
    ) : (
      <i className="fas fa-times-circle text-danger" title="DNS record needs attention"></i>
    );
  };

  return (
    <Modal onClose={onClose} title="DNS Instructions" size="large">
      <div className="dns-instructions">
        <div className="instructions-header">
          <h3>{instructions.title}</h3>
          <p>{instructions.description}</p>
          
          {domain.domain_type === 'custom_domain' && (
            <div className="domain-info">
              <strong>Domain:</strong> {domain.full_domain || domain.domain_name}
            </div>
          )}
        </div>

        {instructions.isAutomatic ? (
          <div className="automatic-config">
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <div>
                <strong>Automatic Configuration</strong>
                <p>This subdomain is automatically configured and managed by our system. No manual DNS configuration is required.</p>
              </div>
            </div>
            
            <div className="subdomain-details">
              <div className="detail-item">
                <label>Full Domain:</label>
                <span className="domain-value">{domain.full_domain}</span>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard(domain.full_domain, 'domain')}
                  title="Copy to clipboard"
                >
                  {copiedField === 'domain' ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className="fas fa-copy"></i>
                  )}
                </button>
              </div>
              
              <div className="detail-item">
                <label>Status:</label>
                <span className={`status-badge status-${domain.status}`}>
                  {domain.status.charAt(0).toUpperCase() + domain.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="manual-config">
            <div className="dns-records-section">
              <h4>Required DNS Records</h4>
              
              {domain.domain_type === 'custom_domain' && dnsResults && (
                <div className="dns-status">
                  <button
                    onClick={checkDnsStatus}
                    disabled={loading}
                    className="btn-secondary btn-sm"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Checking...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync"></i>
                        Refresh DNS Status
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className="records-table">
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Value</th>
                      <th>TTL</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instructions.records.map((record, index) => (
                      <tr key={index}>
                        <td>
                          <span className={`record-type type-${record.type.toLowerCase()}`}>
                            {record.type}
                          </span>
                        </td>
                        <td>
                          <code className="record-name">
                            {record.name === '@' ? `${domain.domain_name}` : `${record.name}.${domain.domain_name}`}
                          </code>
                        </td>
                        <td>
                          <code className="record-value">{record.value}</code>
                        </td>
                        <td>{record.ttl || 300}</td>
                        <td>
                          {getDnsStatusIcon(record)}
                        </td>
                        <td>
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(record.value, `record-${index}`)}
                            title="Copy value"
                          >
                            {copiedField === `record-${index}` ? (
                              <i className="fas fa-check"></i>
                            ) : (
                              <i className="fas fa-copy"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="setup-steps">
              <h4>Setup Steps</h4>
              <ol>
                <li>
                  <strong>Access your domain's DNS settings</strong>
                  <p>Log in to your domain registrar or DNS provider (GoDaddy, Namecheap, Cloudflare, etc.)</p>
                </li>
                <li>
                  <strong>Add the DNS records</strong>
                  <p>Create the DNS records listed above in your DNS management panel</p>
                </li>
                <li>
                  <strong>Wait for propagation</strong>
                  <p>DNS changes can take up to 24-48 hours to propagate globally</p>
                </li>
                <li>
                  <strong>Verify the configuration</strong>
                  <p>Use the "Refresh DNS Status" button to check if your records are correctly configured</p>
                </li>
              </ol>
            </div>

            {dnsResults && (
              <div className="dns-results">
                <h4>Current DNS Status</h4>
                
                <div className="status-grid">
                  <div className="status-item">
                    <label>Points to Server:</label>
                    <span className={dnsResults.pointing_to_server ? 'status-success' : 'status-error'}>
                      {dnsResults.pointing_to_server ? (
                        <>
                          <i className="fas fa-check"></i>
                          Yes
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times"></i>
                          No
                        </>
                      )}
                    </span>
                  </div>
                  
                  <div className="status-item">
                    <label>A Records Found:</label>
                    <span>{dnsResults.a_records?.length || 0}</span>
                  </div>
                  
                  <div className="status-item">
                    <label>CNAME Records:</label>
                    <span>{dnsResults.cname_records?.length || 0}</span>
                  </div>
                </div>

                {dnsResults.error && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    DNS Check Error: {dnsResults.error}
                  </div>
                )}
              </div>
            )}

            <div className="troubleshooting">
              <h4>Troubleshooting</h4>
              <div className="troubleshooting-items">
                <div className="troubleshooting-item">
                  <strong>DNS not propagating?</strong>
                  <p>DNS changes can take time. Use online DNS checkers to verify propagation status.</p>
                </div>
                <div className="troubleshooting-item">
                  <strong>Records not working?</strong>
                  <p>Ensure you're adding records to the correct zone and that TTL values are reasonable (300-3600 seconds).</p>
                </div>
                <div className="troubleshooting-item">
                  <strong>Still having issues?</strong>
                  <p>Contact support with your domain name and DNS configuration for assistance.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DnsInstructions;